import { useRef, useState } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { MessageCircle, ClipboardList, Truck } from "lucide-react";
import Reveal from "./Reveal";

const STEPS = [
  {
    num: "01",
    icon: MessageCircle,
    title: "Hubungi Kami",
    desc: "Chat kami melalui WhatsApp dan sampaikan kebutuhan proyek Anda.",
    testid: "step-hubungi",
  },
  {
    num: "02",
    icon: ClipboardList,
    title: "Sampaikan Kebutuhan",
    desc: "Informasikan jumlah steger dan lokasi proyek.",
    testid: "step-kebutuhan",
  },
  {
    num: "03",
    icon: Truck,
    title: "Steger Diantar",
    desc: "Kami menyiapkan dan mengantar steger ke lokasi proyek Anda.",
    testid: "step-diantar",
  },
];

const HowToOrder = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.8", "end 0.55"],
  });
  const [active, setActive] = useState(0);

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(v < 0.25 ? 0 : v < 0.6 ? 1 : v < 0.92 ? 2 : 3);
  });

  return (
    <section
      id="cara-order"
      ref={ref}
      data-testid="how-to-order-section"
      className="border-b border-[#E5E2DA] bg-[#F7F5F0] py-20 lg:py-28"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-tech mb-4 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#525866]">
              <span className="inline-block h-[2px] w-10 bg-[#DF9F00]" />
              How to Order
              <span className="inline-block h-[2px] w-10 bg-[#DF9F00]" />
            </p>
            <h2 className="font-display text-3xl font-semibold uppercase tracking-wide text-[#141619] sm:text-4xl lg:text-5xl">
              CARA ORDER
            </h2>
          </div>
          <p className="max-w-md text-base leading-relaxed text-[#525866]">
            Proses sederhana untuk mendapatkan steger sesuai kebutuhan proyek
            Anda.
          </p>
        </Reveal>

        <div className="relative mt-16">
          <div className="absolute left-0 right-0 top-[23px] hidden h-[2px] bg-[#E5E2DA] md:block" />
          <motion.div
            data-testid="order-progress-line"
            style={{ scaleX: scrollYProgress }}
            className="absolute left-0 right-0 top-[23px] hidden h-[2px] origin-left bg-[#DF9F00] md:block"
          />
          <div className="absolute bottom-0 left-[23px] top-0 w-[2px] bg-[#E5E2DA] md:hidden" />
          <motion.div
            style={{ scaleY: scrollYProgress }}
            className="absolute bottom-0 left-[23px] top-0 w-[2px] origin-top bg-[#DF9F00] md:hidden"
          />

          <div className="grid gap-12 md:grid-cols-3 md:gap-8">
            {STEPS.map((s, i) => {
              const isActive = active > i;
              return (
                <Reveal key={s.num} delay={i * 0.12}>
                  <div data-testid={s.testid} className="relative pl-20 md:pl-0">
                    <div
                      className={`absolute left-0 top-0 z-10 flex h-12 w-12 items-center justify-center rounded-xl border transition-all duration-500 md:relative md:mb-10 ${
                        isActive
                          ? "scale-110 border-[#DF9F00] bg-[#DF9F00] shadow-[0_8px_24px_rgba(223,159,0,0.35)]"
                          : "border-[#E5E2DA] bg-white"
                      }`}
                    >
                      <s.icon
                        className={`h-5 w-5 transition-colors duration-500 ${
                          isActive ? "text-[#141619]" : "text-[#DF9F00]"
                        }`}
                        strokeWidth={1.75}
                      />
                    </div>
                    <span
                      className={`font-display block text-6xl font-semibold leading-none tracking-wide transition-colors duration-500 lg:text-7xl ${
                        isActive ? "text-[#DF9F00]" : "text-[#141619]/10"
                      }`}
                    >
                      {s.num}
                    </span>
                    <h3 className="font-display mt-4 text-xl font-semibold uppercase tracking-wide text-[#141619]">
                      {s.title}
                    </h3>
                    <p className="mt-3 max-w-xs text-sm leading-relaxed text-[#525866]">
                      {s.desc}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToOrder;
